import { FormInstance, Spin, message } from "antd";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  APIError,
  EconomicIndicator,
  FormFieldConfig,
} from "../helpers/shared-interfaces";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { AxiosResponse } from "axios";
import {
  GET_ECONOMIC_INDICATOR,
  UPDATE_ECONOMIC_INDICATOR,
} from "../gql/economicIndicators";
import moment from "moment";

interface RouteParams {
  id: string;
}

interface State {
  id: string;
  isLoading: boolean;
  date: string;
  economicIndicator?: EconomicIndicator;
  inflation: number;
  tbill: number;
}

export default class EditEconomicIndicator extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      id: "",
      date: "",
      isLoading: true,
      inflation: 0,
      tbill: 0,
    };
    this.reference = React.createRef<FormInstance>();
    this.getEconomicIndicator = this.getEconomicIndicator.bind(this);
    this.updateEconomicIndicator = this.updateEconomicIndicator.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getEconomicIndicator();
    });
  }

  getEconomicIndicator = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_ECONOMIC_INDICATOR,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const economicIndicator = data.data?.economicIndicator;
            this.setState({
              economicIndicator: economicIndicator,
            });
          } else {
            message.error("Failed to get economic indicator data");
          }
        });
      },
    );
  };

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  redirectToViewEconomicIndicatorPage(): void {
    this.props.history.push(`/economic-indicators/${this.state.id}`);
  }

  async updateEconomicIndicator(): Promise<void> {
    const { id } = this.state;
    interface Response {
      data?: {
        updateEconomicIndicator: {
          economicIndicator: EconomicIndicator;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "date",
        "inflation",
        "tbill",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_ECONOMIC_INDICATOR,
        variables: {
          id,
          ...data,
        },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateEconomicIndicator",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Economic indicator updated sucessfully");
        this.redirectToViewEconomicIndicatorPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to update economic indicator - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  render(): JSX.Element | null {
    const { isLoading, economicIndicator } = this.state;
    const fields: FormFieldConfig[] = [
      {
        id: "date",
        name: "date",
        rule: "object",
        inputType: "defaultDate",
        required: true,
        initialValue: economicIndicator?.date
          ? moment(economicIndicator?.date)
          : "",
        label: "Date",
        placeholder: "Date",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 7,
        },
      },
      {
        name: "inflation",
        rule: "number",
        inputType: "number",
        required: true,
        label: "Inflation Rate %",
        placeholder: "Inflation Rate %",
        initialValue: economicIndicator?.inflation,
        elementStyle: { width: "100%" },
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        name: "tbill",
        rule: "number",
        inputType: "number",
        required: true,
        label: "Treasury Bill Rate %",
        placeholder: "Treasury Bill Rate %",
        elementStyle: { width: "100%" },
        initialValue: economicIndicator?.tbill,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "updateEconomicIndicator",
        name: "updateEconomicIndicatorButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateEconomicIndicator,
      },
    ];

    return (
      <Card header="UPDATE ECONOMIC INDICATOR">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
